import React, { useEffect, useState } from "react";
import { PageHeader, Modal, Toast, PasswordInput } from "@/components/Layout";
import api from "@/lib/api";
import useFilters from "@/lib/useFilters";
import FilterActions from "@/components/FilterActions";
import MultiSelect from "@/components/MultiSelect";
import PurgeButton from "@/components/PurgeButton";
import RowActions from "@/components/RowActions";
import { NoAutofillInput, AutofillDecoys } from "@/components/NoAutofill";
import Select from "@/components/Select";
import CoverageHint from "@/components/CoverageHint";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import ViewOnly from "@/components/ViewOnly";
import { lock } from "@/lib/perm";

const ROLE_LABELS = {
  admin:"Administrator",
  hr:"HR",
  manager:"Manager",
  employee:"Salariat",
};
const ROLES = ["admin","hr","manager","employee"];
const EMAIL_RE = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;

export default function Users() {
  const { has, hasModule } = useAuth();
  const didacticLicensed = hasModule("stat_didactic");
  const nav = useNavigate();
  const [users, setUsers] = useState([]);
  const [units, setUnits] = useState([]);
  const [allEmpl, setAllEmpl] = useState(null);   // rapoartele de muncă vizibile persoanei curente (null = nu are dreptul să le vadă)
  const [profileCatalog, setProfileCatalog] = useState([]);
  const [editing, setEditing] = useState(null);
  const [userProfiles, setUserProfiles] = useState([]);
  const [newProfile, setNewProfile] = useState({ profile_id: "", scope_org_unit_id: "", personnel_categories: [] });
  const [categories, setCategories] = useState([]);
  const [pwdReset, setPwdReset] = useState(null);
  const [newPw, setNewPw] = useState("");
  const [grant, setGrant] = useState(null);       // { user, email, password }
  const [revoke, setRevoke] = useState(null); // utilizator căruia i se retrage accesul
  const [actorPw, setActorPw] = useState("");
  const [mfaReset, setMfaReset] = useState(null);   // { user } — dispozitiv pierdut: resetează autentificarea în doi pași
  const [grantDone, setGrantDone] = useState(null); // { username, password }
  const [msg, setMsg] = useState({ text: "", type: "info" });
  const [sel, setSel] = useState(() => new Set());      // ids bifate pentru acțiuni în masă
  const [bulk, setBulk] = useState(null);                // { mode: "grant"|"revoke" } — confirmare
  const [bulkBusy, setBulkBusy] = useState(false);
  const [bulkDone, setBulkDone] = useState(null);       // { mode, data } — rezultatul
  // filtre cu „Aplică filtre”: implicit se afișează toți utilizatorii
  const uf = useFilters({ last: "", first: "", cats: [], access: "" });   // access: "" = toți | "da" | "nu"
  const fLast = uf.applied.last, fFirst = uf.applied.first, fCats = uf.applied.cats, fAccess = uf.applied.access;

  // Fiecare sursă se încarcă separat: dacă una lipsește (fără drept), lista de utilizatori apare totuși.
  const load = () => {
    api.get("/users").then(r => setUsers(r.data)).catch(() => {});
    api.get("/mdm/org-units").then(r => setUnits(r.data)).catch(() => {});
    if (has("employments.view")) api.get("/mdm/employments").then(r => setAllEmpl(r.data)).catch(() => setAllEmpl(null));
    if (has("profiles.view")) api.get("/access/profiles").then(r => setProfileCatalog(r.data.filter(p => p.kind !== "system"))).catch(() => setProfileCatalog([]));
  };
  useEffect(() => { load(); api.get("/config").then(r => setCategories(r.data?.personnel_categories || [])).catch(() => {}); }, []);

  const loadUserProfiles = (uid) => !has("profiles.view") ? setUserProfiles([]) : api.get(`/access/users/${uid}`).then(r => setUserProfiles(r.data.memberships)).catch(() => setUserProfiles([]));

  const openNew = () => {
    setUserProfiles([]);
    setEditing({
      username:"", password:"", first_name:"", last_name:"",
      cnp:"", email:"", phone:"", active:true, access_enabled:true,
      ...(didacticLicensed ? { sfd: { education_seniority_start: "" } } : {}),
      roles: [{ role: "employee", scope_org_unit_id: null }],  // implicit: Salariat global
    });
  };

  const openEdit = (u, view = false) => {
    loadUserProfiles(u.id);
    setEditing({
      ...u, view, password:"",
      ...(didacticLicensed ? { sfd: { education_seniority_start: u.sfd?.education_seniority_start || "", seniority: u.sfd?.seniority } } : {}),
      roles: (u.roles || []).map(r => ({
        id: r.id, role: r.role, scope_org_unit_id: r.scope_org_unit_id || null
      }))
    });
  };

  const addProfile = async () => {
    if (!newProfile.profile_id) return;
    try {
      await api.post(`/access/profiles/${newProfile.profile_id}/members`, {
        user_id: editing.id,
        scope_org_unit_id: newProfile.scope_org_unit_id || null,
        personnel_categories: newProfile.personnel_categories,
      });
      setNewProfile({ profile_id: "", scope_org_unit_id: "", personnel_categories: [] });
      loadUserProfiles(editing.id);
    } catch (e) {
      setMsg({ text: e?.response?.data?.detail || "Eroare", type: "error" });
    }
  };

  const removeProfile = async (m) => {
    try {
      await api.delete(`/access/profiles/${m.profile_id}/members/${m.id}`);
      loadUserProfiles(editing.id);
    } catch (e) {
      setMsg({ text: e?.response?.data?.detail || "Eroare", type: "error" });
    }
  };

  const save = async () => {
    // Validări client
    const noAccess = !editing.id && editing.access_enabled === false;
    if (!noAccess && !editing.username?.trim()) return setMsg({text:"Utilizator (login) obligatoriu",type:"error"});
    if (!editing.first_name?.trim() || !editing.last_name?.trim())
      return setMsg({text:"Nume și prenume sunt obligatorii",type:"error"});
    if (!editing.id && !noAccess && (!editing.password || editing.password.length < 12))
      return setMsg({text:"Parola trebuie să aibă minim 12 caractere",type:"error"});
    if (editing.id && editing.password && editing.password.length < 12)
      return setMsg({text:"Parola trebuie să aibă minim 12 caractere",type:"error"});
    if (editing.id && editing.password && !editing.actor_password)
      return setMsg({text:"Pentru a schimba parola altui cont, reintroduceți parola dumneavoastră",type:"error"});
    if (editing.email && !EMAIL_RE.test(editing.email))
      return setMsg({text:"Adresă de e-mail invalidă (ex: nume@domeniu.ro)",type:"error"});
    if (!noAccess && (!editing.roles || editing.roles.length === 0))
      return setMsg({text:"Trebuie să atribui cel puțin un rol",type:"error"});

    try {
      if (editing.id) {
        // Update user data
        await api.put(`/users/${editing.id}`, editing);
        // Sync roles: remove existing roles not in new list, add new ones
        const existing = users.find(u => u.id === editing.id)?.roles || [];
        const newKeys = new Set(editing.roles.map(r => `${r.role}::${r.scope_org_unit_id||""}`));
        for (const r of existing) {
          const key = `${r.role}::${r.scope_org_unit_id||""}`;
          if (!newKeys.has(key)) await api.delete(`/users/${editing.id}/roles/${r.id}`);
        }
        const oldKeys = new Set(existing.map(r => `${r.role}::${r.scope_org_unit_id||""}`));
        for (const r of editing.roles) {
          const key = `${r.role}::${r.scope_org_unit_id||""}`;
          if (!oldKeys.has(key)) await api.post(`/users/${editing.id}/roles`,
            { role: r.role, scope_org_unit_id: r.scope_org_unit_id || null });
        }
      } else {
        // Create user with roles in one shot
        await api.post("/users", editing);
      }
      setEditing(null); load();
      setMsg({text:"Salvat",type:"success"}); setTimeout(()=>setMsg({text:""}), 1500);
    } catch (e) {
      setMsg({text: e?.response?.data?.detail || "Eroare", type: "error"});
    }
  };

  const doReset = async () => {
    if (newPw.length < 12) { setMsg({text:"Parola trebuie să aibă minim 12 caractere",type:"error"}); return; }
    if (!actorPw) { setMsg({text:"Reintroduceți parola dumneavoastră pentru a continua",type:"error"}); return; }
    try {
      await api.post(`/users/${pwdReset.id}/reset-password`, { new_password: newPw, actor_password: actorPw });
      setPwdReset(null); setNewPw(""); setActorPw("");
      setMsg({text:"Parolă resetată. Utilizatorul trebuie să o schimbe la prima logare.",type:"success"});
      setTimeout(()=>setMsg({text:""}), 3000);
    } catch(e) { setMsg({text:e?.response?.data?.detail||"Eroare",type:"error"}); }
  };

  const doRevoke = async () => {
    try {
      await api.post(`/users/${revoke.id}/revoke-access`);
      setRevoke(null); load();
      setMsg({text:"Acces retras. Persoana rămâne înregistrată, cu rapoartele de muncă.",type:"success"});
      setTimeout(()=>setMsg({text:""}), 3000);
    } catch(e) { setRevoke(null); setMsg({text:e?.response?.data?.detail||"Eroare",type:"error"}); }
  };

  const doGrant = async () => {
    if (grant.email && !EMAIL_RE.test(grant.email)) { setMsg({text:"Adresă de e-mail invalidă (ex: nume@domeniu.ro)",type:"error"}); return; }
    if (grant.password && grant.password.length < 12) { setMsg({text:"Parola trebuie să aibă minim 12 caractere",type:"error"}); return; }
    try {
      const r = await api.post(`/users/${grant.user.id}/grant-access`, { email: grant.email, password: grant.password });
      setGrantDone({ username: r.data.username, password: r.data.temporary_password, emailed: !!grant.email });
      setGrant(null); load();
    } catch(e) { setMsg({text:e?.response?.data?.detail||"Eroare",type:"error"}); }
  };

  // Helpers pentru gestiunea rolurilor în modal
  const addRoleRow = () => setEditing({...editing, roles: [...(editing.roles||[]), { role:"employee", scope_org_unit_id:null }]});
  const updRole = (i, patch) => {
    const rs = [...editing.roles]; rs[i] = {...rs[i], ...patch}; setEditing({...editing, roles: rs});
  };
  const delRole = (i) => setEditing({...editing, roles: editing.roles.filter((_,idx)=>idx!==i)});

  const fold = (x) => String(x ?? "").normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase().trim();
  const filtered = users.filter(u =>
    (!fLast || fold(u.last_name).includes(fold(fLast))) &&
    (!fFirst || fold(u.first_name).includes(fold(fFirst))) &&
    (!fCats.length || (u.personnel_categories || []).some(c => fCats.includes(c))) &&
    (!fAccess || (fAccess === "da") === (u.access_enabled !== false)));
  const anyFilter = !!(fLast || fFirst || fCats.length || fAccess);
  const toggleSel = (id) => setSel(prev => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const allVisibleSelected = filtered.length > 0 && filtered.every(u => sel.has(u.id));
  const toggleAllVisible = () => setSel(prev => {
    const n = new Set(prev);
    if (allVisibleSelected) filtered.forEach(u => n.delete(u.id)); else filtered.forEach(u => n.add(u.id));
    return n;
  });
  const selectedUsers = users.filter(u => sel.has(u.id));
  const selNoAccess = selectedUsers.filter(u => u.access_enabled === false).length;
  const selWithAccess = selectedUsers.length - selNoAccess;
  const runBulk = async () => {
    setBulkBusy(true);
    try {
      const ids = selectedUsers.map(u => u.id);
      const r = await api.post(bulk.mode === "grant" ? "/users/bulk-grant-access" : "/users/bulk-revoke-access", { user_ids: ids });
      setBulkDone({ mode: bulk.mode, data: r.data }); setBulk(null); setSel(new Set()); load();
    } catch (e) { setBulk(null); setMsg({ text: e?.response?.data?.detail || "Eroare", type: "error" }); }
    finally { setBulkBusy(false); }
  };
  const downloadCreds = () => {
    const rows = bulkDone.data.results.filter(r => r.ok).map(r => [r.name, r.username, r.temporary_password, r.email || ""]);
    const csv = ["Nume;Utilizator;Parolă temporară;E-mail", ...rows.map(r => r.map(c => `"${String(c).replace(/"/g, '""')}"`).join(";"))].join("\r\n");
    const a = document.createElement("a");
    a.href = URL.createObjectURL(new Blob(["\ufeff" + csv], { type: "text/csv;charset=utf-8" }));
    a.download = "date_acces_utilizatori.csv"; a.click(); URL.revokeObjectURL(a.href);
  };

  return (
    <>
      <PageHeader title="Utilizatori & Roluri" subtitle={anyFilter ? `${filtered.length} din ${users.length} conturi` : `${users.length} conturi`}
        actions={<button className="btn btn-primary" data-testid="add-user" onClick={openNew} {...lock(has("users.create"))}>+ Utilizator nou</button>} />
      <Toast msg={msg.text} type={msg.type} />
      <form className="card" style={{padding:12,marginBottom:12,display:"flex",flexWrap:"wrap",gap:10,alignItems:"flex-end"}} data-testid="users-filters"
        onSubmit={e=>{e.preventDefault(); uf.apply();}}>
        <div><label className="label">Nume</label>
          <input className="input" value={uf.draft.last} onChange={e=>uf.set({last:e.target.value})} placeholder="Caută după nume" data-testid="filter-last-name" style={{minWidth:160}} /></div>
        <div><label className="label">Prenume</label>
          <input className="input" value={uf.draft.first} onChange={e=>uf.set({first:e.target.value})} placeholder="Caută după prenume" data-testid="filter-first-name" style={{minWidth:160}} /></div>
        <div><label className="label">Categorie de personal</label>
          <MultiSelect options={categories} value={uf.draft.cats} onChange={v=>uf.set({cats:v})} allLabel="Toate categoriile" testId="filter-categories" /></div>
        <div><label className="label">Acces în platformă</label>
          <Select className="select" value={uf.draft.access} onChange={e=>uf.set({access:e.target.value})} data-testid="filter-access">
            <option value="">Toți</option><option value="da">Cu acces</option><option value="nu">Fără acces</option>
          </Select></div>
        <FilterActions f={uf} testid="users-filters" />
      </form>
      {selectedUsers.length > 0 && (
        <div className="card" style={{padding:"10px 14px",marginBottom:12,display:"flex",flexWrap:"wrap",gap:10,alignItems:"center",background:"var(--tint-50)",border:"1px solid var(--tint-200)"}} data-testid="bulk-bar">
          <b>{selectedUsers.length} selectați</b>
          <span style={{fontSize:12,color:"#475569"}}>({selNoAccess} fără acces, {selWithAccess} cu acces)</span>
          <span style={{flex:1}} />
          <button className="btn btn-primary" onClick={()=>setBulk({mode:"grant"})} data-testid="bulk-grant" {...lock(has("users.update") && !!selNoAccess)}>Acordă acces ({selNoAccess})</button>
          <button className="btn btn-danger" onClick={()=>setBulk({mode:"revoke"})} data-testid="bulk-revoke" {...lock(has("users.update") && !!selWithAccess)}>Revocă acces ({selWithAccess})</button>
          <button className="btn btn-secondary" onClick={()=>setSel(new Set())}>Deselectează</button>
        </div>
      )}
      <div className="card">
        <table className="tbl">
          <thead><tr><th style={{width:34}}><input type="checkbox" checked={allVisibleSelected} onChange={toggleAllVisible} title="Selectează / deselectează toate persoanele din listă (după filtre)" data-testid="select-all" /></th><th>Utilizator</th><th>Nume și prenume</th><th>CNP</th><th>Email</th><th>Roluri</th><th>Status</th><th></th></tr></thead>
          <tbody>{filtered.map(u => (
            <tr key={u.id} data-testid={`user-row-${u.username}`}>
              <td><input type="checkbox" checked={sel.has(u.id)} onChange={()=>toggleSel(u.id)} data-testid={`select-${u.username}`} /></td>
              <td style={{fontWeight:600}}>
                {u.username}
                {u.access_enabled === false && <span title="Persoană înregistrată fără cont de autentificare" style={{marginLeft:6,fontSize:10,padding:"1px 6px",background:"#E2E8F0",color:"#334155",borderRadius:3}}>FĂRĂ ACCES</span>}
                {u.must_change_password && <span title="Trebuie să schimbe parola" style={{marginLeft:6,fontSize:10,padding:"1px 6px",background:"#FEF3C7",color:"#92400E",borderRadius:3}}>SCHIMBĂ PW</span>}
              </td>
              <td>{u.last_name} {u.first_name}</td>
              <td className="tabular-nums" style={{color:"#64748B",fontSize:12}}>{u.cnp || "—"}</td>
              <td style={{color:"#64748B",fontSize:12}}>{u.email || "—"}</td>
              <td>
                <div style={{display:"flex",flexWrap:"wrap",gap:4}}>
                  {u.roles.map(r => {
                    const scopeName = r.scope_org_unit_id ? (units.find(o=>o.id===r.scope_org_unit_id)?.name || "?") : null;
                    return (
                      <span key={r.id} className="badge badge-active"
                            title={scopeName ? `Scope pe structura: ${scopeName}` : "Rol global"}>
                        {ROLE_LABELS[r.role]}
                        {scopeName && <span style={{fontSize:10,opacity:0.75,marginLeft:4}}>· {scopeName}</span>}
                      </span>
                    );
                  })}
                  {(u.profiles || []).map(sr => {
                    const scopeName = sr.scope_org_unit_id ? (units.find(o=>o.id===sr.scope_org_unit_id)?.name || "?") : "Toată platforma";
                    const cats = (sr.personnel_categories || []).length ? ` · ${sr.personnel_categories.join(", ")}` : "";
                    return (
                      <span key={sr.id} className="badge" data-testid={`user-profile-${u.username}`}
                            title={`Profil de acces: ${sr.profile_name} — ${scopeName}${cats}${sr.valid_to ? ` · până la ${sr.valid_to}` : ""}`}
                            style={{background:"#F5F3FF",color:"#5B21B6",border:"1px solid #DDD6FE"}}>
                        {sr.profile_name}
                        <span style={{fontSize:10,opacity:0.75,marginLeft:4}}>· {scopeName}{cats}</span>
                      </span>
                    );
                  })}
                  {u.roles.length===0 && !(u.profiles||[]).length && <span style={{color:"#94A3B8",fontSize:12}}>—</span>}
                </div>
              </td>
              <td>{u.active ? <span className="badge badge-active">Activ</span> : <span className="badge badge-inactive">Inactiv</span>}</td>
              <td>
                <RowActions testid={`row-menu-${u.username}`} items={[
                  {label:"Vizualizează", icon:"eye", onClick:()=>openEdit(u, true), testid:`view-${u.username}`},
                  {label:"Editează", icon:"edit", onClick:()=>openEdit(u), testid:`edit-${u.username}`, ...lock(has("users.update"))},
                  has("users.access_check") && {label:"Verifică accesul", icon:"shield", title:"Ce poate face persoana și de ce nu vede anumite date", onClick:()=>nav(`/admin/access?verifica=${u.id}`), testid:`diag-${u.username}`},
                  u.access_enabled === false
                    ? {label:"Acordă acces", icon:"key", primary:true, onClick:()=>setGrant({user:u,email:u.email||"",password:""}), testid:`grant-${u.username}`, ...lock(has("users.update"))}
                    : {label:"Resetează PW", icon:"key", onClick:()=>setPwdReset(u), ...lock(has("users.reset_password"))},
                  u.access_enabled !== false && {label:"Deblochează 2FA", icon:"shield", onClick:()=>{setMfaReset(u); setActorPw("");}, testid:`mfa-reset-${u.username}`, ...lock(has("users.reset_password"))},
                  u.access_enabled !== false && {label:"Revocă acces", icon:"close", danger:true, onClick:()=>setRevoke(u), testid:`revoke-${u.username}`, ...lock(has("users.update"))},
                ]}>
                  <PurgeButton entity="user" id={u.id} onDone={load} style={{padding:"3px 10px"}} />
                </RowActions>
              </td>
            </tr>
          ))}</tbody>
        </table>
      </div>

      {editing && (
        <Modal title={editing.view ? "Utilizator — vizualizare" : editing.id ? "Editează utilizator" : "Utilizator nou"} onClose={()=>setEditing(null)} width={760}>
          <ViewOnly on={editing.view}>
          <div className="grid-2">
            <div style={{gridColumn:"1/-1"}}><AutofillDecoys /></div>
            <div style={{gridColumn:"1/-1"}}><label className={"label"+(!editing.id && editing.access_enabled===false ? "" : " label-req")}>Utilizator (login){!editing.id && editing.access_enabled===false && " — opțional, se generează din nume"}</label>
              <NoAutofillInput className="input" autoComplete="off" name="onomad-user-login" value={editing.username} disabled={!!editing.id} onChange={e=>setEditing({...editing,username:e.target.value})} data-testid="u-username" /></div>
            <div><label className="label label-req">Nume</label>
              <NoAutofillInput className="input" autoComplete="off" name="onomad-user-last" value={editing.last_name} onChange={e=>setEditing({...editing,last_name:e.target.value})} data-testid="u-last" /></div>
            <div><label className="label label-req">Prenume</label>
              <NoAutofillInput className="input" autoComplete="off" name="onomad-user-first" value={editing.first_name} onChange={e=>setEditing({...editing,first_name:e.target.value})} data-testid="u-first" /></div>
            <div><label className="label">CNP</label>
              <NoAutofillInput className="input" autoComplete="off" name="onomad-user-cnp" value={editing.cnp||""} onChange={e=>setEditing({...editing,cnp:e.target.value})} data-testid="u-cnp" /></div>
            <div><label className="label">Email</label>
              <NoAutofillInput className="input" type="email" autoComplete="off" name="onomad-user-email" value={editing.email||""} placeholder="nume@domeniu.ro"
                onChange={e=>setEditing({...editing,email:e.target.value})} data-testid="u-email" /></div>
            <div><label className="label">Telefon</label>
              <NoAutofillInput className="input" type="tel" autoComplete="off" name="onomad-user-phone" value={editing.phone||""} onChange={e=>setEditing({...editing,phone:e.target.value})} data-testid="u-phone" /></div>
            {!editing.id && (
              <div style={{gridColumn:"1 / -1"}}><label style={{display:"flex",gap:8,alignItems:"center",fontSize:13}}>
                <input type="checkbox" checked={editing.access_enabled===false} data-testid="u-no-access"
                  onChange={e=>setEditing({...editing,access_enabled:!e.target.checked})} />
                Fără acces în platformă (doar înregistrare pentru rapoarte de muncă/pontaj; accesul se poate acorda ulterior)
              </label></div>
            )}
            {editing.access_enabled !== false && (
            <div><label className={"label"+(editing.id?"":" label-req")}>Parolă {editing.id ? "(lasă gol dacă nu schimbi)" : "(minim 12 caractere)"}</label>
              <PasswordInput guard value={editing.password||""} onChange={e=>setEditing({...editing,password:e.target.value})} testId="u-pw" autoComplete="new-password" name="onomad-user-new-password" data-lpignore="true" data-1p-ignore="true" /></div>
            )}
            {editing.id && editing.password && (
            <div><label className="label label-req">Parola dumneavoastră (pentru confirmare)</label>
              <PasswordInput value={editing.actor_password||""} onChange={e=>setEditing({...editing,actor_password:e.target.value})} testId="u-actor-pw" autoComplete="current-password" name="onomad-actor-password" /></div>
            )}
            <div><label className="label">Status</label>
              <Select className="select" value={editing.active?"true":"false"} onChange={e=>setEditing({...editing,active:e.target.value==="true"})}>
                <option value="true">Activ</option><option value="false">Inactiv</option>
              </Select></div>
          </div>

          {/* Rapoarte de muncă (contractele) legate de această persoană — doar afișare, se gestionează în Rapoarte de Muncă */}
          {editing.id && allEmpl && (() => {
            const mine = allEmpl.filter(e => e.user_id === editing.id)
              .sort((a, b) => (a.status === "terminated") - (b.status === "terminated") || String(b.start_date).localeCompare(String(a.start_date)));
            const STATUS = { active: ["Activ", "badge-active"], suspended: ["Suspendat", ""], terminated: ["Încetat", "badge-inactive"] };
            return (
              <div style={{marginTop:20,borderTop:"1px solid #E2E8F0",paddingTop:16}} data-testid="user-employments">
                <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:6}}>
                  <div style={{fontSize:13,fontWeight:700,color:"var(--navy)"}}>Rapoarte de muncă ({mine.length})</div>
                </div>
                <div style={{fontSize:11,color:"#64748B",marginBottom:8}}>Contractele de muncă legate de această persoană. Se modifică din „Rapoarte de Muncă".</div>
                {mine.length === 0
                  ? <div style={{fontSize:12,color:"#94A3B8"}}>Nu are niciun raport de muncă.</div>
                  : <div style={{overflowX:"auto"}}><table className="tbl">
                      <thead><tr><th>Marcă</th><th>Structură</th><th>Funcție</th><th>Contract</th><th>Perioadă</th><th>Status</th></tr></thead>
                      <tbody>{mine.map(e => {
                        const st = STATUS[e.status] || [e.status, ""];
                        return (
                          <tr key={e.id} data-testid={`user-empl-${e.marca}`}>
                            <td style={{fontWeight:600}} className="tabular-nums">{e.marca}</td>
                            <td>{units.find(u => u.id === e.org_unit_id)?.name || "—"}</td>
                            <td>{e.position_title || "—"}</td>
                            <td>{[e.contract_type_label, e.contract_number && `nr. ${e.contract_number}`].filter(Boolean).join(" ") || "—"}{e.reges_duration === "Determinata" ? " · determinată" : ""}</td>
                            <td className="tabular-nums" style={{whiteSpace:"nowrap"}}>{e.start_date || "—"} → {e.end_date || "prezent"}</td>
                            <td><span className={"badge " + st[1]}>{st[0]}</span></td>
                          </tr>);
                      })}</tbody>
                    </table></div>}
              </div>);
          })()}

          {/* Vechimea în învățământ (modulul Stat de Funcții Didactic); celelalte vechimi se completează în raportul de muncă */}
          {didacticLicensed && editing.sfd && (
            <div style={{marginTop:16,borderTop:"1px solid #E2E8F0",paddingTop:12}}>
              <div style={{fontSize:12,fontWeight:700,color:"#334155",marginBottom:8}}>Vechime în învățământ</div>
              <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12}}>
                <div><label className="label">Data început vechime în învățământ</label>
                  <input className="input" type="date" value={editing.sfd.education_seniority_start||""} onChange={e=>setEditing({...editing,sfd:{...editing.sfd,education_seniority_start:e.target.value}})} data-testid="u-edu-start" />
                  {editing.id && editing.sfd.seniority?.higher_ed && <div style={{fontSize:11,color:"#64748B",marginTop:4}}>Calculat: <b>{editing.sfd.seniority.higher_ed.years} ani {editing.sfd.seniority.higher_ed.months} luni</b></div>}</div>
              </div>
              <div style={{fontSize:11,color:"#64748B",marginTop:6}}>Vechimile în muncă, în specialitate și la angajator se completează din raportul de muncă (fila „Date Contract și ReGES”).</div>
            </div>
          )}

          {/* Roluri — integrat în același modal */}
          <div style={{marginTop:20,borderTop:"1px solid #E2E8F0",paddingTop:16}}>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:10}}>
              <div>
                <div className="label label-req" style={{fontSize:13,fontWeight:700,color:"var(--navy)",margin:0}}>Roluri atribuite</div>
                <div style={{fontSize:11,color:"#64748B",marginTop:2}}>
                  Câmp obligatoriu — minim un rol. „Global" = pe toată platforma. „Structură" = doar pe acea unitate + subunități.
                </div>
              </div>
              <button className="btn btn-secondary" style={{padding:"4px 10px",fontSize:12}} onClick={addRoleRow} data-testid="add-role-row" {...lock(has("users.roles_assign"))}>+ Adaugă rol</button>
            </div>
            <div style={{display:"grid",gap:6}}>
              {(editing.roles||[]).map((r, i) => (
                <div key={i} style={{display:"grid", gridTemplateColumns:"1fr 1fr auto", gap:8, alignItems:"center"}}>
                  <Select className="select" value={r.role} onChange={e=>updRole(i,{role:e.target.value})} data-testid={`role-select-${i}`}>
                    {ROLES.map(k=><option key={k} value={k}>{ROLE_LABELS[k]}</option>)}
                  </Select>
                  <Select className="select" value={r.scope_org_unit_id||""}
                    onChange={e=>updRole(i,{scope_org_unit_id: e.target.value || null})}
                    data-testid={`role-scope-${i}`}>
                    <option value="">Global (toată platforma)</option>
                    {units.filter(u=>u.active).map(u=>(
                      <option key={u.id} value={u.id}>N{u.level} · {u.name}</option>
                    ))}
                    {units.filter(u=>u.active).length === 0 && (
                      <option value="" disabled>— nicio structură organizatorică definită încă —</option>
                    )}
                  </Select>
                  <button className="btn btn-danger" style={{padding:"4px 8px",fontSize:11}} onClick={()=>delRole(i)} data-testid={`role-del-${i}`} {...lock(has("users.roles_revoke"))}>×</button>
                </div>
              ))}
              {(editing.roles||[]).length === 0 && (
                <div style={{padding:12, background:"#FEE2E2", color:"#991B1B", borderRadius:6, fontSize:12}}>
                  Nu ai atribuit niciun rol. Adaugă cel puțin unul.
                </div>
              )}
            </div>
            <div style={{marginTop:12, padding:"8px 12px", background:"var(--tint-50)", borderRadius:6, fontSize:11.5, color:"var(--navy-3)"}}>
              ℹ️ <b>Managerii care își fac și propria programare</b> au nevoie de 2 roluri: <b>„Manager"</b> pe structura pe care o gestionează + <b>„Salariat"</b> global (pentru propriile CO-uri).
            </div>
          </div>

          {/* Profiluri de acces — drepturi alocate suplimentar peste rol, pe o structură și/sau pe categorii de personal */}
          <div style={{marginTop:20,borderTop:"1px solid #E2E8F0",paddingTop:16}}>
            <div style={{fontSize:13,fontWeight:700,color:"var(--navy)",marginBottom:2}}>Profiluri de acces</div>
            <div style={{fontSize:11,color:"#64748B",marginBottom:10}}>
              Drepturi suplimentare peste rol. „Toată platforma” = fără restricție; altfel doar pe structura aleasă + subunități. Opțional, restrângi și pe categorii de personal (gol = toate). Profilurile și perioada lor se gestionează din Parametrii → Acces.
            </div>
            {!editing.id ? (
              <div style={{padding:12, background:"#F1F5F9", color:"#475569", borderRadius:6, fontSize:12}}>
                Profilurile se alocă după crearea contului — salvează utilizatorul, apoi redeschide-l pentru editare.
              </div>
            ) : (
              <>
                <div style={{display:"grid",gap:6,marginBottom:10}}>
                  {userProfiles.map(us => (
                    <div key={us.id} style={{display:"flex",alignItems:"center",gap:8}} data-testid={`user-profile-${us.id}`}>
                      <span className="badge badge-active">{us.profile_name}</span>
                      <span style={{fontSize:11,color: us.active === false ? "#991B1B" : "#64748B"}}>
                        {us.scope_org_unit_id ? (units.find(o=>o.id===us.scope_org_unit_id)?.name || "?") : "Toată platforma"}
                        {(us.personnel_categories || []).length > 0 && ` · ${us.personnel_categories.join(", ")}`}
                        {(us.valid_from || us.valid_to) && ` · ${us.valid_from || "…"} – ${us.valid_to || "…"}`}
                        {us.active === false && " (inactiv)"}
                      </span>
                      {has("profiles.assign") && <button className="btn btn-danger" style={{padding:"2px 8px",fontSize:11,marginLeft:"auto"}} onClick={()=>removeProfile(us)}>×</button>}
                    </div>
                  ))}
                  {userProfiles.length === 0 && <div style={{fontSize:12,color:"#94A3B8"}}>Niciun profil alocat.</div>}
                </div>
                {has("profiles.assign") && <>
                <div style={{display:"grid", gridTemplateColumns:"1fr 1fr auto", gap:8, alignItems:"center"}}>
                  <Select className="select" value={newProfile.profile_id} onChange={e=>setNewProfile({...newProfile, profile_id:e.target.value})} data-testid="new-profile-select">
                    <option value="">— alege profilul —</option>
                    {profileCatalog.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                  </Select>
                  <Select className="select" value={newProfile.scope_org_unit_id} onChange={e=>setNewProfile({...newProfile, scope_org_unit_id:e.target.value})} data-testid="new-profile-scope">
                    <option value="">Toată platforma</option>
                    {units.filter(u=>u.active).map(u=>(
                      <option key={u.id} value={u.id}>N{u.level} · {u.name}</option>
                    ))}
                  </Select>
                  <button className="btn btn-secondary" style={{padding:"4px 10px",fontSize:12}} onClick={addProfile} data-testid="add-profile-assignment">+ Alocă</button>
                </div>
                <div style={{marginTop:8,maxWidth:420}}>
                  <MultiSelect options={categories} value={newProfile.personnel_categories}
                    onChange={v=>setNewProfile({...newProfile, personnel_categories:v})}
                    allLabel="Toate categoriile de personal" testId="new-profile-categories" minWidth={260} />
                </div>
                {newProfile.profile_id && <CoverageHint scopeIds={newProfile.scope_org_unit_id ? [newProfile.scope_org_unit_id] : []} categories={newProfile.personnel_categories} />}
                {profileCatalog.length === 0 && (
                  <div style={{marginTop:8,fontSize:11,color:"#94A3B8"}}>Niciun profil de acces definit încă — creează unul din Parametrii → Acces.</div>
                )}
                </>}
              </>
            )}
          </div>

          </ViewOnly>

          <div style={{display:"flex",gap:8,justifyContent:"flex-end",marginTop:18}}>
            <button className="btn btn-secondary" onClick={()=>setEditing(null)}>{editing.view ? "Închide" : "Anulează"}</button>
            {!editing.view && <button className="btn btn-primary" onClick={save} data-testid="save-user" {...lock(has(editing.id ? "users.update" : "users.create"))}>Salvează</button>}
          </div>
        </Modal>
      )}

      {grant && (
        <Modal title={`Acordă acces — ${grant.user.last_name} ${grant.user.first_name}`} onClose={()=>setGrant(null)}>
          <div style={{marginBottom:12,fontSize:13,color:"#334155"}}>
            Se activează contul „{grant.user.username}”. Parola e temporară și va trebui schimbată la prima autentificare.
          </div>
          <label className="label">E-mail (opțional — dacă e completat, primește datele pe e-mail)</label>
          <NoAutofillInput className="input" type="email" autoComplete="off" name="onomad-grant-email" value={grant.email} onChange={e=>setGrant({...grant,email:e.target.value})} data-testid="grant-email" />
          <label className="label" style={{marginTop:10}}>Parolă temporară (lasă gol pentru una generată)</label>
          <NoAutofillInput className="input" type="text" autoComplete="off" name="onomad-grant-password" value={grant.password} onChange={e=>setGrant({...grant,password:e.target.value})} data-testid="grant-pw" />
          <div style={{display:"flex",gap:8,justifyContent:"flex-end",marginTop:16}}>
            <button className="btn btn-secondary" onClick={()=>setGrant(null)}>Anulează</button>
            <button className="btn btn-primary" onClick={doGrant} data-testid="confirm-grant">Acordă acces</button>
          </div>
        </Modal>
      )}

      {revoke && (
        <Modal title={`Revocă acces — ${revoke.last_name} ${revoke.first_name}`} onClose={()=>setRevoke(null)}>
          <div style={{fontSize:13,color:"#334155",lineHeight:1.6}}>
            Persoana nu se va mai putea autentifica (sesiunea curentă se închide), dar rămâne înregistrată,
            cu rapoartele de muncă și pontajul. Accesul se poate acorda din nou oricând.
          </div>
          <div style={{display:"flex",gap:8,justifyContent:"flex-end",marginTop:16}}>
            <button className="btn btn-secondary" onClick={()=>setRevoke(null)}>Anulează</button>
            <button className="btn btn-danger" onClick={doRevoke} data-testid="confirm-revoke">Revocă acces</button>
          </div>
        </Modal>
      )}

      {bulk && (
        <Modal title={bulk.mode === "grant" ? `Acordă acces — ${selNoAccess} persoane` : `Revocă acces — ${selWithAccess} persoane`} onClose={()=>setBulk(null)}>
          <div style={{fontSize:13,color:"#334155",lineHeight:1.6}}>
            {bulk.mode === "grant" ? (<>
              Pentru fiecare persoană selectată <b>fără acces</b> se generează o parolă temporară, care trebuie schimbată la prima autentificare.
              Persoanele care au deja acces sunt ignorate. La final primești lista cu utilizatorii și parolele (se afișează o singură dată).
              <div style={{marginTop:8,color:"#64748B"}}>Dacă opțiunea „Trimite pe e-mail datele de acces” (Email SMTP) este bifată, fiecare primește și un e-mail; cine nu are adresa de e-mail completată nu primește acces și apare cu eroare în raport.</div>
            </>) : (<>
              Persoanele selectate <b>cu acces</b> nu se vor mai putea autentifica (sesiunile curente se închid), dar rămân înregistrate, cu rapoartele de muncă și pontajul.
              Accesul se poate acorda din nou oricând. Persoanele care nu au acces sunt ignorate; propriul cont nu poate fi revocat.
            </>)}
          </div>
          <div style={{display:"flex",gap:8,justifyContent:"flex-end",marginTop:16}}>
            <button className="btn btn-secondary" onClick={()=>setBulk(null)}>Anulează</button>
            <button className={`btn ${bulk.mode === "grant" ? "btn-primary" : "btn-danger"}`} disabled={bulkBusy} onClick={runBulk} data-testid="bulk-confirm">
              {bulkBusy ? "Se procesează..." : bulk.mode === "grant" ? "Acordă acces" : "Revocă acces"}
            </button>
          </div>
        </Modal>
      )}

      {bulkDone && (() => {
        const d = bulkDone.data, ok = d.results.filter(r => r.ok), bad = d.results.filter(r => !r.ok);
        const grant = bulkDone.mode === "grant";
        return (
          <Modal title={grant ? "Acces acordat" : "Acces revocat"} onClose={()=>setBulkDone(null)}>
            <div style={{fontSize:13,color:"#334155",lineHeight:1.6}} data-testid="bulk-result">
              <b>{ok.length}</b> {grant ? "persoane au primit acces" : "persoane și-au pierdut accesul"}{bad.length ? <>, <b style={{color:"#991B1B"}}>{bad.length}</b> nu au putut fi procesate</> : null}.
              {grant && ok.length > 0 && (d.emailed
                ? <div>Datele de acces au fost trimise pe e-mail fiecărei persoane.</div>
                : <div>Parolele temporare sunt afișate <b>o singură dată</b> — descarcă lista și transmite-le persoanelor.</div>)}
            </div>
            {grant && ok.length > 0 && !d.emailed && (
              <div style={{maxHeight:260,overflow:"auto",marginTop:10,border:"1px solid #E2E8F0",borderRadius:6}}>
                <table className="tbl"><thead><tr><th>Nume</th><th>Utilizator</th><th>Parolă temporară</th></tr></thead>
                  <tbody>{ok.map(r => <tr key={r.id}><td>{r.name}</td><td>{r.username}</td><td style={{fontFamily:"monospace"}}>{r.temporary_password}</td></tr>)}</tbody></table>
              </div>
            )}
            {bad.length > 0 && (
              <div style={{maxHeight:200,overflow:"auto",marginTop:10,border:"1px solid #FECACA",background:"#FEF2F2",borderRadius:6,padding:"8px 12px",fontSize:12,color:"#991B1B"}} data-testid="bulk-errors">
                {bad.map(r => <div key={r.id}><b>{r.name}</b> — {r.error}</div>)}
              </div>
            )}
            <div style={{display:"flex",gap:8,justifyContent:"flex-end",marginTop:16}}>
              {grant && ok.length > 0 && !d.emailed && <button className="btn btn-secondary" onClick={downloadCreds} data-testid="bulk-download">Descarcă lista (CSV)</button>}
              <button className="btn btn-primary" onClick={()=>setBulkDone(null)}>Închide</button>
            </div>
          </Modal>
        );
      })()}

      {grantDone && (
        <Modal title="Acces acordat" onClose={()=>setGrantDone(null)}>
          <div style={{fontSize:13,color:"#334155",lineHeight:1.6}}>
            Utilizator: <b>{grantDone.username}</b><br/>
            Parolă temporară: <b data-testid="grant-result-pw">{grantDone.password}</b><br/>
            {grantDone.emailed ? "Datele au fost trimise și pe e-mail. " : ""}
            Parola nu va mai fi afișată — comunic-o persoanei acum.
          </div>
          <div style={{display:"flex",justifyContent:"flex-end",marginTop:16}}>
            <button className="btn btn-primary" onClick={()=>setGrantDone(null)}>Am notat</button>
          </div>
        </Modal>
      )}

      {mfaReset && (
        <Modal title={`Deblochează verificarea în doi pași — ${mfaReset.username}`} onClose={()=>{setMfaReset(null);setActorPw("");}}>
          <div style={{marginBottom:12,fontSize:13,color:"#334155"}}>
            Anulează codurile trimise și blocarea după coduri greșite. La următoarea autentificare persoana primește un cod nou pe e-mail. Persoana va fi anunțată.
          </div>
          <label className="label label-req">Parola dumneavoastră (pentru confirmare)</label>
          <PasswordInput value={actorPw} onChange={e=>setActorPw(e.target.value)} testId="mfa-reset-actor-pw" autoComplete="current-password" name="onomad-actor-password" />
          <div style={{display:"flex",gap:8,justifyContent:"flex-end",marginTop:16}}>
            <button className="btn btn-secondary" onClick={()=>{setMfaReset(null);setActorPw("");}}>Anulează</button>
            <button className="btn btn-danger" data-testid="mfa-reset-confirm" onClick={async()=>{
              try { await api.post(`/users/${mfaReset.id}/mfa/reset`, { actor_password: actorPw }); setMfaReset(null); setActorPw(""); load();
                setMsg({text:"Verificarea în doi pași a fost deblocată.",type:"success"}); setTimeout(()=>setMsg({text:""}),3000); }
              catch(e){ setMsg({text:e?.response?.data?.detail||"Eroare",type:"error"}); }
            }}>Deblochează</button>
          </div>
        </Modal>
      )}

      {pwdReset && (
        <Modal title={`Resetează parola — ${pwdReset.username}`} onClose={()=>{setPwdReset(null);setNewPw("");setActorPw("");}}>
          <div style={{marginBottom:12,fontSize:13,color:"#334155"}}>
            Utilizatorul va fi obligat să schimbe parola la prima logare.
          </div>
          <label className="label label-req">Parola nouă (minim 12 caractere, nu una des folosită)</label>
          <NoAutofillInput className="input" type="text" autoComplete="off" name="onomad-reset-password" value={newPw} onChange={e=>setNewPw(e.target.value)} data-testid="reset-pw" />
          <label className="label label-req" style={{marginTop:10}}>Parola dumneavoastră (pentru confirmare)</label>
          <PasswordInput value={actorPw} onChange={e=>setActorPw(e.target.value)} testId="reset-actor-pw" autoComplete="current-password" name="onomad-actor-password" />
          <div style={{marginTop:8,fontSize:12,color:"#64748b"}}>Persoana vizată va fi anunțată că parola ei a fost schimbată.</div>
          <div style={{display:"flex",gap:8,justifyContent:"flex-end",marginTop:16}}>
            <button className="btn btn-secondary" onClick={()=>{setPwdReset(null);setNewPw("");setActorPw("");}}>Anulează</button>
            <button className="btn btn-danger" onClick={doReset} data-testid="confirm-reset">Resetează</button>
          </div>
        </Modal>
      )}
    </>
  );
}
